/**
 * Compare two Ace trials — highlight RAG and check diffs with drill-down.
 */

import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faChevronDown,
  faChevronRight,
  faCodeCompare,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import LoadingPlaceholder from "@/components/ui/loading-placeholder/LoadingPlaceholder";
import { agentReviewAPI, type AceTrialCompareResult, type AceTrialSummary } from "@/services/api/agentReview";
import { aceTrialOptionLabel, aceTrialRunBadge, aceProfileLabel } from "./aceTrialLabels";
import { ragOption } from "./RagOption";
import type { CheckAnswer, Rag, ReviewPhase } from "./types";

interface AceTrialDiffPanelProps {
  reviewId: string;
  trials: AceTrialSummary[];
  activeTrialId?: string | null;
  refreshToken?: number;
  onViewTrial: (trialId: string) => void;
}

function answerLabel(answer: CheckAnswer | null | undefined): string {
  if (!answer) return "—";
  if (answer === "pass") return "Pass";
  if (answer === "fail") return "Fail";
  return "N/A";
}

function RagPill({ rag }: { rag: Rag | null | undefined }) {
  if (!rag) {
    return <span className="text-xs text-slate-500">—</span>;
  }
  const opt = ragOption(rag);
  return (
    <span className={clsx("inline-flex items-center gap-1 text-xs font-medium", opt.text)}>
      <span className={clsx("w-1.5 h-1.5 rounded-full", opt.dot)} aria-hidden />
      {opt.label}
    </span>
  );
}

function trialOptionLabel(trial: AceTrialSummary): string {
  return aceTrialOptionLabel(trial);
}

const PHASE_LABELS: Record<ReviewPhase, string> = {
  orchestration: "Orchestrate",
  output: "Output",
};

export function AceTrialDiffPanel({
  reviewId,
  trials,
  activeTrialId = null,
  refreshToken = 0,
  onViewTrial,
}: AceTrialDiffPanelProps) {
  const [open, setOpen] = useState(false);
  const [baselineId, setBaselineId] = useState("");
  const [compareId, setCompareId] = useState("");
  const [diff, setDiff] = useState<AceTrialCompareResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedCheck, setExpandedCheck] = useState<string | null>(null);

  const sortedTrials = useMemo(
    () => [...trials].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()),
    [trials],
  );

  useEffect(() => {
    if (sortedTrials.length < 2) return;

    const primary = sortedTrials.find((t) => t.is_canonical) ?? sortedTrials[0];
    const latest = sortedTrials[sortedTrials.length - 1];
    const active = activeTrialId ? sortedTrials.find((t) => t.trial_id === activeTrialId) : null;

    const baseline = primary.trial_id;
    let compare = latest.trial_id;
    if (compare === baseline) {
      const fallback = sortedTrials[sortedTrials.length - 2];
      if (fallback) compare = fallback.trial_id;
    }
    if (active && active.trial_id !== baseline) {
      compare = active.trial_id;
    }

    setBaselineId(baseline);
    setCompareId(compare);
  }, [sortedTrials, activeTrialId, refreshToken]);

  useEffect(() => {
    if (!open || !baselineId || !compareId || baselineId === compareId) {
      setDiff(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void agentReviewAPI
      .compareAceTrials(reviewId, baselineId, compareId)
      .then((result) => {
        if (!cancelled) setDiff(result);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't compare these Ace trials.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, reviewId, baselineId, compareId, refreshToken]);

  if (trials.length < 2) return null;

  return (
    <div className="rounded-xl ring-1 ring-amber-500/20 bg-amber-500/[0.03] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.02]"
      >
        <FontAwesomeIcon icon={faCodeCompare} className="text-amber-300 text-sm shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-200">Compare Ace runs</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Spot differences between two trials — defaults to primary (Run #1) vs latest re-run
          </p>
        </div>
        <FontAwesomeIcon
          icon={open ? faChevronDown : faChevronRight}
          className="text-slate-500 text-xs shrink-0"
        />
      </button>

      {open ? (
        <div className="border-t border-amber-500/15 px-4 py-3 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
                Baseline run
              </label>
              <select
                value={baselineId}
                onChange={(e) => setBaselineId(e.target.value)}
                className="w-full rounded-lg bg-slate-900/60 px-3 py-2 text-sm text-slate-200 ring-1 ring-white/10"
              >
                {sortedTrials.map((trial) => (
                  <option key={trial.trial_id} value={trial.trial_id}>
                    {trialOptionLabel(trial)}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
                Compare run
              </label>
              <select
                value={compareId}
                onChange={(e) => setCompareId(e.target.value)}
                className="w-full rounded-lg bg-slate-900/60 px-3 py-2 text-sm text-slate-200 ring-1 ring-white/10"
              >
                {sortedTrials.map((trial) => (
                  <option key={trial.trial_id} value={trial.trial_id}>
                    {trialOptionLabel(trial)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {baselineId === compareId ? (
            <p className="text-sm text-amber-300/90">Pick two different runs to compare.</p>
          ) : null}

          {loading ? <LoadingPlaceholder text="Comparing runs…" /> : null}
          {error ? <p className="text-sm text-rose-400">{error}</p> : null}

          {diff && !loading ? (
            <div className="space-y-3">
              <div className="rounded-lg bg-white/[0.02] px-3 py-3 ring-1 ring-white/5 space-y-2">
                <p className="text-sm text-slate-200">{diff.headline}</p>
                {!diff.same_criteria_version ? (
                  <p className="text-[11px] text-amber-300/90">
                    These trials used different criteria versions — check diffs may include checks only
                    present in one version.
                  </p>
                ) : null}
                {diff.same_ace_profile === false ? (
                  <p className="text-[11px] text-amber-300/90">
                    Grading profiles differ ({aceProfileLabel(diff.baseline.ace_profile)} vs{" "}
                    {aceProfileLabel(diff.compare.ace_profile)}) — expect more passes on the lenient
                    side.
                  </p>
                ) : null}
                <div className="flex flex-wrap gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => onViewTrial(diff.baseline.trial_id)}
                    className="text-[11px] text-indigo-300 hover:text-indigo-200"
                  >
                    View {diff.baseline.run_number ? aceTrialRunBadge(diff.baseline.run_number) : "baseline"}
                  </button>
                  <button
                    type="button"
                    onClick={() => onViewTrial(diff.compare.trial_id)}
                    className="text-[11px] text-indigo-300 hover:text-indigo-200"
                  >
                    View {diff.compare.run_number ? aceTrialRunBadge(diff.compare.run_number) : "compare run"}
                  </button>
                </div>
              </div>

              {diff.phase_diffs.some((d) => d.changed) ? (
                <div className="space-y-2">
                  <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
                    Phase RAG changes
                  </p>
                  {diff.phase_diffs
                    .filter((d) => d.changed)
                    .map((d) => (
                      <div
                        key={d.phase}
                        className="rounded-lg bg-white/[0.02] px-3 py-2.5 ring-1 ring-white/5 flex flex-wrap items-center gap-2 text-sm"
                      >
                        <span className="text-slate-400">{PHASE_LABELS[d.phase]}</span>
                        <RagPill rag={d.baseline_rag} />
                        <FontAwesomeIcon icon={faArrowRight} className="text-slate-600 text-xs" />
                        <RagPill rag={d.compare_rag} />
                      </div>
                    ))}
                </div>
              ) : null}

              {diff.check_diffs.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
                    Check differences ({diff.check_diffs.length})
                  </p>
                  {diff.check_diffs.map((row) => {
                    const key = `${row.phase}:${row.check_id}`;
                    const expanded = expandedCheck === key;
                    return (
                      <div
                        key={key}
                        className="rounded-lg bg-white/[0.02] ring-1 ring-white/5 overflow-hidden"
                      >
                        <button
                          type="button"
                          onClick={() => setExpandedCheck(expanded ? null : key)}
                          className="flex w-full items-start gap-2 px-3 py-2.5 text-left hover:bg-white/[0.02]"
                        >
                          <FontAwesomeIcon
                            icon={expanded ? faChevronDown : faChevronRight}
                            className="text-slate-500 text-xs mt-1 shrink-0"
                          />
                          <div className="min-w-0 flex-1 space-y-1">
                            <p className="text-xs text-slate-500">{PHASE_LABELS[row.phase]}</p>
                            <p className="text-sm text-slate-200">{row.check_label}</p>
                            <div className="flex flex-wrap items-center gap-2 text-xs">
                              <span className="text-slate-500">{answerLabel(row.baseline_answer)}</span>
                              <FontAwesomeIcon icon={faArrowRight} className="text-slate-600 text-[10px]" />
                              <span className="text-amber-200">{answerLabel(row.compare_answer)}</span>
                              {row.only_in_baseline ? (
                                <span className="text-[10px] text-slate-500">(only in baseline criteria)</span>
                              ) : null}
                              {row.only_in_compare ? (
                                <span className="text-[10px] text-slate-500">(only in compare criteria)</span>
                              ) : null}
                            </div>
                          </div>
                        </button>
                        {expanded ? (
                          <div className="border-t border-white/5 px-3 py-3 grid gap-3 sm:grid-cols-2 text-xs">
                            <div className="space-y-1">
                              <p className="font-medium text-slate-500">Baseline reasoning</p>
                              <p className="text-slate-300 leading-relaxed">
                                {row.baseline_reason || "No reason recorded."}
                              </p>
                            </div>
                            <div className="space-y-1">
                              <p className="font-medium text-slate-500">Compare reasoning</p>
                              <p className="text-slate-300 leading-relaxed">
                                {row.compare_reason || "No reason recorded."}
                              </p>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : diff.total_changes === 0 ? (
                <p className="text-sm text-emerald-300/90">No check-level differences between these runs.</p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
